import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ProductLogo } from '@/shared/ui/brand';

export type BrandStatusTone = 'brand' | 'coral' | 'amber';

export interface BrandStatusShellProps {
  children: ReactNode;
  /** Full viewport takeover vs nested in app canvas. */
  layout?: 'viewport' | 'embedded';
  tone?: BrandStatusTone;
  /** Optional status pill in the top-right corner. */
  badge?: ReactNode;
  showLogo?: boolean;
  className?: string;
}

const BLOB_A = {
  brand:
    'radial-gradient(closest-side, color-mix(in srgb, var(--color-primary) 10%, transparent), transparent 70%)',
  coral:
    'radial-gradient(closest-side, color-mix(in srgb, var(--color-coral-500) 10%, transparent), transparent 70%)',
  amber:
    'radial-gradient(closest-side, color-mix(in srgb, var(--color-coral-500) 10%, transparent), transparent 70%)',
} as const;

const BLOB_B = {
  brand:
    'radial-gradient(closest-side, color-mix(in srgb, var(--color-brand-400) 8%, transparent), transparent 70%)',
  coral:
    'radial-gradient(closest-side, color-mix(in srgb, var(--color-coral-500) 8%, transparent), transparent 70%)',
  amber:
    'radial-gradient(closest-side, color-mix(in srgb, var(--color-primary) 8%, transparent), transparent 70%)',
} as const;

/**
 * Full-page chrome for loaders and global error/status pages.
 * Matches `templates/404.html` / `templates/500.html` layout.
 */
export function BrandStatusShell({
  children,
  layout = 'viewport',
  tone = 'brand',
  badge,
  showLogo = true,
  className,
}: BrandStatusShellProps): JSX.Element {
  const isViewport = layout === 'viewport';

  return (
    <div
      className={cn(
        'relative flex flex-col items-center justify-center overflow-hidden px-6 py-12',
        isViewport ? 'fixed inset-0 z-50 min-h-screen bg-[var(--app-bg)]' : 'min-h-[400px] w-full',
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <div className={cn('bs-grid pointer-events-none absolute inset-0', `bs-grid-${tone}`)} />

      <div
        className="pointer-events-none absolute top-0 right-0 h-[500px] w-[500px] translate-x-[30%] -translate-y-[30%] rounded-full"
        style={{ background: BLOB_A[tone] }}
      />
      <div
        className="pointer-events-none absolute bottom-0 left-0 h-[400px] w-[400px] -translate-x-[30%] translate-y-[30%] rounded-full"
        style={{ background: BLOB_B[tone] }}
      />

      {showLogo && isViewport ? (
        <div className="absolute top-6 left-6 z-10">
          <Link to="/" className="flex items-center gap-2.5 no-underline">
            <ProductLogo size="md" />
            <div className="font-display text-xl font-semibold tracking-tight text-ink-900">
              DentXE
            </div>
          </Link>
        </div>
      ) : null}

      {badge ? <div className="absolute top-6 right-6 z-10">{badge}</div> : null}

      <div className="relative z-10 w-full max-w-lg text-center">{children}</div>
    </div>
  );
}
